import React, { useState, useEffect } from 'react';
import { apiGet } from '../hooks/useApi';
import { useApi } from '../hooks/useApi';
import { useSubscription } from '../hooks/useSubscription';
import { CardSkeleton, ChartSkeleton } from '../components/LoadingSkeleton';
import UpgradeModal from '../components/UpgradeModal';
import ProBadge from '../components/ui/ProBadge';
import { buildApiUrl } from '../lib/apiClient';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { Download, FileText, Lock } from 'lucide-react';

const makeEaKey = (ea) => `${ea.account_id || 0}:${ea.magic || 0}:${encodeURIComponent((ea.ea_name || '').trim())}`;
const reportTypeParam = (accountType) => accountType && accountType !== 'all' ? `&type_filter=${accountType}` : '';

export default function Reports() {
  const [tab, setTab] = useState('performance');
  const [period, setPeriod] = useState('30d');
  const [account, setAccount] = useState('all');
  const [accountType, setAccountType] = useState('all');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const { data: accounts } = useApi('/accounts');
  const { hasProAccess, isFree } = useSubscription();
  const visibleAccounts = React.useMemo(() => {
    if (accountType === 'real') return (accounts || []).filter(a => !a.is_demo);
    if (accountType === 'demo') return (accounts || []).filter(a => a.is_demo);
    return accounts || [];
  }, [accounts, accountType]);

  useEffect(() => {
    if (account !== 'all' && !visibleAccounts.some(a => String(a.id) === String(account))) setAccount('all');
  }, [account, visibleAccounts]);

  return (
    <div className="space-y-6 animate-stagger">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-100">Performance Report</h1>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-dark-border overflow-hidden bg-dark-card">
            {['all', 'real', 'demo'].map(value => (
              <button
                key={value}
                onClick={() => setAccountType(value)}
                className={`px-3 py-1.5 text-xs font-semibold ${accountType === value ? 'bg-accent-green text-dark-bg' : 'text-gray-400 hover:text-white'}`}
              >
                {value === 'all' ? 'All' : value === 'real' ? 'Real' : 'Demo'}
              </button>
            ))}
          </div>
          <select
            value={account}
            onChange={e => setAccount(e.target.value)}
            className="bg-dark-card border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
          >
            <option value="all">All Accounts</option>
            {visibleAccounts.map(a => (
              <option key={a.id} value={a.id}>{a.broker} #{a.account_number} {a.is_demo ? '[DEMO]' : '[REAL]'}</option>
            ))}
          </select>
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="bg-dark-card border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300"
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="ytd">Year to Date</option>
            <option value="all">All Time</option>
          </select>
          <button
            onClick={() => window.open(buildApiUrl(`/api/reports/export/csv/trades?period=${period}&account=${account}${reportTypeParam(accountType)}`), '_blank')}
            className="flex items-center gap-1 px-3 py-1.5 bg-accent-blue text-white rounded-lg text-xs font-medium"
          >
            <Download size={14} /> CSV
          </button>
          <button
            onClick={() => {
              if (isFree) { setUpgradeFeature('pdf_export'); setShowUpgradeModal(true); return; }
              window.open(buildApiUrl(`/api/reports/export/pdf?period=${period}&account=${account}${reportTypeParam(accountType)}`), '_blank');
            }}
            className="flex items-center gap-1 px-3 py-1.5 bg-accent-red text-white rounded-lg text-xs font-medium"
          >
            <FileText size={14} /> PDF {isFree && <ProBadge />}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'performance', label: 'Performance' },
          { id: 'comparison', label: 'EA Comparison' },
          { id: 'broker', label: 'Broker' },
          { id: 'annual', label: 'Annual' },
          { id: 'projections', label: 'Projections' },
          { id: 'analytics', label: 'Analytics' },
          { id: 'quant', label: 'Quant' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              tab === t.id ? 'bg-accent-green text-dark-bg' : 'bg-dark-card text-gray-400 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'performance' && <PerformanceTab period={period} account={account} accountType={accountType} />}
      {tab === 'comparison' && <ComparisonTab period={period} account={account} accountType={accountType} />}
      {tab === 'broker' && isFree ? (
        <div className="bg-dark-card border border-dark-border rounded-lg p-8 text-center">
          <Lock size={32} className="text-gray-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Broker Comparison</h3>
          <p className="text-gray-400 mb-4">This feature is available on upgraded plans.</p>
          <button
            onClick={() => { setUpgradeFeature('broker_comparison'); setShowUpgradeModal(true); }}
            className="px-4 py-2 bg-accent-purple text-white rounded-lg text-sm font-medium"
          >
            Upgrade Plan
          </button>
        </div>
      ) : (
        tab === 'broker' && <BrokerComparisonTab account={account} accountType={accountType} />
      )}
      {tab === 'annual' && <AnnualStatsTab account={account} accountType={accountType} />}
      {tab === 'projections' && <ProjectionsTab account={account} accountType={accountType} />}
      {tab === 'analytics' && <AnalyticsTab period={period} account={account} accountType={accountType} />}
      {tab === 'quant' && <QuantTab period={period} account={account} accountType={accountType} />}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
    </div>
  );
}

// === PERFORMANCE TAB ===
function PerformanceTab({ period, account, accountType }) {
  const [data, setData] = useState(null);
  const [breakdown, setBreakdown] = useState('symbol');
  const [breakdownData, setBreakdownData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performanceError, setPerformanceError] = useState('');
  const [breakdownError, setBreakdownError] = useState('');

  useEffect(() => {
    setLoading(true);
    setPerformanceError('');
    apiGet(`/reports/performance?period=${period}&account=${account}${reportTypeParam(accountType)}`)
      .then(setData)
      .catch(() => setPerformanceError('Performance data could not be refreshed. Please retry.'))
      .finally(() => setLoading(false));
  }, [period, account, accountType]);

  useEffect(() => {
    const endpoint = breakdown === 'symbol' ? '/reports/by-symbol' :
                     breakdown === 'ea' ? '/reports/by-ea' :
                     breakdown === 'hour' ? '/reports/by-hour' : '/reports/by-day-of-week';
    setBreakdownError('');
    apiGet(`${endpoint}?period=${period}&account=${account}${reportTypeParam(accountType)}`)
      .then(setBreakdownData)
      .catch(() => setBreakdownError('This breakdown could not be refreshed. Showing the latest available data.'));
  }, [breakdown, period, account, accountType]);

  if (loading) return <ChartSkeleton />;
  if (performanceError && !data) return <div className="card text-center py-10 text-amber-300">{performanceError}</div>;
  const s = data?.stats || {};

  return (
    <div className="space-y-6 animate-stagger">
      {/* Date Range Info */}
      {data?.date_range && (
        <div className="text-xs text-gray-500">
          Showing data from <span className="text-gray-300 font-medium">{data.date_range.start || 'All time'}</span> to <span className="text-gray-300 font-medium">{data.date_range.end || 'Now'}</span>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatBox label="NET P&L" value={`$${(s.net_pnl || 0).toLocaleString(undefined, {minimumFractionDigits:2})}`}
          color={s.net_pnl >= 0 ? 'text-accent-green' : 'text-accent-red'} sub={`${s.winning_trades || 0}W / ${s.losing_trades || 0}L`} />
        <StatBox label="TOTAL TRADES" value={s.total_trades || 0} sub={`Avg: $${(s.expectancy || 0).toFixed(2)}/trade`} />
        <StatBox label="WIN RATE" value={`${s.win_rate || 0}%`} color={s.win_rate >= 50 ? 'text-accent-green' : 'text-accent-red'} sub={`PF: ${s.profit_factor || 0}`} />
        <StatBox label="PROFIT FACTOR" value={s.profit_factor || 0} color="text-accent-purple" />
        <StatBox label="MAX DRAWDOWN" value={`$${(s.max_drawdown || 0).toFixed(2)}`} color="text-accent-red" sub={`Avg: $${(s.avg_drawdown || 0).toFixed(2)}`} />
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatBox label="Gross Profit" value={`$${(s.gross_profit || 0).toFixed(2)}`} color="text-accent-green" small />
        <StatBox label="Gross Loss" value={`$${(s.gross_loss || 0).toFixed(2)}`} color="text-accent-red" small />
        <StatBox label="Commission" value={`$${(s.total_commission || 0).toFixed(2)}`} color="text-accent-orange" small />
        <StatBox label="Swap" value={`$${(s.total_swap || 0).toFixed(2)}`} color="text-accent-orange" small />
        <StatBox label="Largest Win" value={`$${(s.largest_win || 0).toFixed(2)}`} color="text-accent-green" small />
        <StatBox label="Largest Loss" value={`$${(s.largest_loss || 0).toFixed(2)}`} color="text-accent-red" small />
        <StatBox label="Avg Duration" value={formatDuration(s.avg_duration_seconds)} small />
      </div>

      {/* Equity Curve */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Equity Curve</h3>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={data?.equity_curve || []}>
            <defs>
              <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a855f7" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
            <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
            <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8 }} />
            <Area type="monotone" dataKey="equity" stroke="#a855f7" fill="url(#eqGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Heatmap */}
      {data?.monthly_heatmap && data.monthly_heatmap.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Monthly Performance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.monthly_heatmap}>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
              <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8 }} />
              <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
                {(data.monthly_heatmap || []).map((entry, i) => (
                  <Cell key={i} fill={entry.profit >= 0 ? '#00ff88' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Performance Breakdown */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-300">Performance Breakdown</h3>
          <div className="flex gap-1">
            {['symbol', 'ea', 'hour', 'day'].map(b => (
              <button
                key={b}
                onClick={() => setBreakdown(b)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                  breakdown === b ? 'bg-accent-green text-dark-bg' : 'bg-dark-bg text-gray-400 hover:text-white'
                }`}
              >
                By {b.charAt(0).toUpperCase() + b.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 border-b border-dark-border text-left text-xs">
                <th className="py-2 px-3">{breakdown === 'hour' ? 'Hour' : breakdown === 'day' ? 'Day' : breakdown === 'ea' ? 'EA' : 'Symbol'}</th>
                <th className="py-2 px-3">Profit</th>
                <th className="py-2 px-3">Trades</th>
                <th className="py-2 px-3">Wins</th>
                <th className="py-2 px-3">Win Rate</th>
                {breakdown === 'symbol' && <th className="py-2 px-3">$/Lot</th>}
              </tr>
            </thead>
            <tbody>
              {(breakdownData || []).map((row, i) => (
                <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-hover">
                  <td className="py-2 px-3 font-bold">
                    {breakdown === 'symbol' ? row.symbol : breakdown === 'ea' ? row.ea_name : breakdown === 'day' ? row.day : (row.hour !== undefined ? `${row.hour}:00` : '')}
                  </td>
                  <td className={`py-2 px-3 font-semibold ${(row.net_profit || row.profit || 0) >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    ${(row.net_profit || row.profit || 0).toFixed(2)}
                  </td>
                  <td className="py-2 px-3">{row.total_trades || row.trades || 0}</td>
                  <td className="py-2 px-3">{row.wins || 0}</td>
                  <td className="py-2 px-3 text-accent-green">{row.win_rate || 0}%</td>
                  {breakdown === 'symbol' && <td className="py-2 px-3">${row.profit_per_lot || 0}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {breakdownError && <p className="text-amber-300 text-center py-3 text-xs">{breakdownError}</p>}
          {(!breakdownData || breakdownData.length === 0) && (
            <p className="text-gray-500 text-center py-6 text-sm">No data for this period</p>
          )}
        </div>
      </div>
    </div>
  );
}

// === EA COMPARISON TAB ===
function ComparisonTab({ period, account, accountType }) {
  const { data: allEAs } = useApi(`/ea-stats?${new URLSearchParams({ ...(account !== 'all' ? { account_id: account } : {}), ...(accountType !== 'all' ? { type_filter: accountType } : {}) }).toString()}`);
  const [selectedEaKeys, setSelectedEaKeys] = useState([]);
  const [comparison, setComparison] = useState(null);

  const eaOptions = React.useMemo(() => {
    return (allEAs || [])
      .map(ea => {
        const name = ea.ea_name || `Magic ${ea.magic}`;
        const key = makeEaKey(ea);
        return {
          ...ea,
          key,
          label: `${name} (#${ea.magic}${ea.account_id ? ` / A${ea.account_id}` : ''})`,
          net_profit: ea.net_profit || 0,
          total_trades: ea.total_trades || 0,
        };
      })
      .sort((a, b) => (b.net_profit || 0) - (a.net_profit || 0));
  }, [allEAs]);

  useEffect(() => {
    if (eaOptions.length > 0 && selectedEaKeys.length === 0) {
      setSelectedEaKeys(eaOptions.slice(0, 5).map(e => e.key));
    }
  }, [eaOptions, selectedEaKeys.length]);

  useEffect(() => {
    if (selectedEaKeys.length === 0) {
      setComparison(null);
      return;
    }
    const selectedParam = selectedEaKeys.map(encodeURIComponent).join(',');
    apiGet(`/reports/ea-comparison?ea_keys=${selectedParam}&period=${period}${account !== 'all' ? `&account=${account}` : ''}${reportTypeParam(accountType)}`)
      .then(setComparison)
      .catch(() => setComparison({ comparison: [] }));
  }, [selectedEaKeys, period, account, accountType]);

  const toggleEaKey = (key) => {
    setSelectedEaKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const rows = comparison?.comparison || [];

  return (
    <div className="space-y-6 animate-stagger">
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300">Select EAs to Compare</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedEaKeys(eaOptions.map(e => e.key))} className="text-xs text-accent-green hover:underline">Select All</button>
            <button onClick={() => setSelectedEaKeys([])} className="text-xs text-gray-400 hover:underline">Clear</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {eaOptions.map(ea => (
            <button
              key={ea.key}
              onClick={() => toggleEaKey(ea.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                selectedEaKeys.includes(ea.key)
                  ? 'bg-accent-green/20 border-accent-green/40 text-accent-green'
                  : 'bg-dark-bg border-dark-border text-gray-400 hover:text-white'
              }`}
            >
              {ea.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card glass-card overflow-x-auto">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Side-by-Side Comparison</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b border-dark-border text-left text-xs">
              <th className="py-2 px-3">EA Name</th>
              <th className="py-2 px-3">Net Profit</th>
              <th className="py-2 px-3">Trades</th>
              <th className="py-2 px-3">Win Rate</th>
              <th className="py-2 px-3">Profit Factor</th>
              <th className="py-2 px-3">Wins</th>
              <th className="py-2 px-3">Losses</th>
              <th className="py-2 px-3">Max DD</th>
              <th className="py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((ea, i) => {
              const isTotal = ea.identity_key === 'total' || (ea.magic === 0 && String(ea.ea_name || '').startsWith('TOTAL'));
              return (
                <tr key={ea.identity_key || `${ea.account_id}-${ea.magic}-${i}`} className={`border-b border-dark-border/50 ${isTotal ? 'bg-dark-bg font-bold' : 'hover:bg-dark-hover'}`}>
                  <td className="py-2 px-3 font-bold">{ea.ea_name}</td>
                  <td className={`py-2 px-3 font-semibold ${(ea.net_profit || 0) >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {(ea.net_profit || 0) >= 0 ? '+' : '-'}${Math.abs(ea.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-2 px-3">{ea.total_trades || 0}</td>
                  <td className="py-2 px-3 text-accent-green">{ea.win_rate || 0}%</td>
                  <td className="py-2 px-3">{ea.profit_factor || '-'}</td>
                  <td className="py-2 px-3 text-accent-green">{ea.winning_trades || 0}</td>
                  <td className="py-2 px-3 text-accent-red">{ea.losing_trades || 0}</td>
                  <td className="py-2 px-3 text-accent-red">${ea.max_drawdown || 0}</td>
                  <td className="py-2 px-3 text-xs text-gray-400">{ea.status || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-gray-500 text-center py-6 text-sm">Select at least one EA to compare.</p>}
      </div>
    </div>
  );
}

// === BROKER COMPARISON TAB ===
function BrokerComparisonTab({ accountType }) {
  const { data: accounts } = useApi('/accounts');
  const { data: eaStats } = useApi(`/ea-stats?${new URLSearchParams(accountType !== 'all' ? { type_filter: accountType } : {}).toString()}`);
  const [brokerData, setBrokerData] = useState(null);
  const [eaBrokerData, setEaBrokerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('overview');
  const [selectedEA, setSelectedEA] = useState('');
  const [compareMetric, setCompareMetric] = useState('net_profit');

  useEffect(() => {
    const scopedAccounts = accountType === 'real' ? (accounts || []).filter(a => !a.is_demo) : accountType === 'demo' ? (accounts || []).filter(a => a.is_demo) : (accounts || []);
    if (!scopedAccounts || scopedAccounts.length === 0) { setLoading(false); return; }
    Promise.all([
      Promise.all(scopedAccounts.map(a => apiGet(`/reports/performance?account=${a.id}&period=all${reportTypeParam(accountType)}`).then(d => ({ ...d, account: a })))),
      apiGet(`/reports/broker-ea-analysis?${new URLSearchParams(accountType !== 'all' ? { type_filter: accountType } : {}).toString()}`),
    ]).then(([results, eaAnalysis]) => {
      const brokers = {};
      results.forEach(r => {
        const broker = r.account.broker || 'Unknown';
        if (!brokers[broker]) brokers[broker] = { broker, accounts: [], totalProfit: 0, totalTrades: 0, totalWins: 0, totalLosses: 0, totalCommission: 0, totalSwap: 0, totalBalance: 0, maxDrawdown: 0 };
        const s = r.stats || {};
        brokers[broker].accounts.push(r.account);
        brokers[broker].totalProfit += s.net_pnl || 0;
        brokers[broker].totalTrades += s.total_trades || 0;
        brokers[broker].totalWins += s.winning_trades || 0;
        brokers[broker].totalLosses += s.losing_trades || 0;
        brokers[broker].totalCommission += s.total_commission || 0;
        brokers[broker].totalSwap += s.total_swap || 0;
        brokers[broker].totalBalance += r.account.balance || 0;
        brokers[broker].maxDrawdown = Math.max(brokers[broker].maxDrawdown, s.max_drawdown || 0);
      });
      setBrokerData(Object.values(brokers));
      setEaBrokerData(eaAnalysis);
      const multi = eaAnalysis?.ea_analysis?.find(e => e.total_brokers > 1);
      if (multi) setSelectedEA(multi.ea_name);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [accounts, accountType]);

  if (loading) return <div className="text-gray-500 text-center py-12">Loading...</div>;
  if (!brokerData || brokerData.length === 0) return <div className="text-gray-500 text-center py-12">No accounts found</div>;
  const sorted = [...brokerData].sort((a, b) => b.totalProfit - a.totalProfit);
  const allEAs = eaBrokerData?.ea_analysis || [];
  const multiBrokerEAs = allEAs.filter(e => e.total_brokers > 1);
  const singleBrokerEAs = allEAs.filter(e => e.total_brokers === 1);
  const selectedData = allEAs.find(e => e.ea_name === selectedEA);

  return (
    <div className="space-y-6 animate-stagger">
      <div className="flex flex-wrap gap-2">
        {[{id:'overview',label:'Overview'},{id:'costs',label:'Instrument Costs'},{id:'per-ea',label:'Per-EA Comparison'},{id:'risk',label:'Risk & Score'},{id:'sessions',label:'Sessions'}].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} className={`px-4 py-2 rounded-lg text-xs font-medium transition ${subTab === t.id ? 'bg-accent-purple text-white' : 'bg-dark-card text-gray-400 hover:text-white'}`}>{t.label}</button>
        ))}
      </div>

      {subTab === 'overview' && (
        <div className="space-y-6 animate-stagger">
          {/* Broker Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sorted.map(b => (
              <div key={b.broker} className="stat-card card-glow">
                <span className="text-xs text-gray-500 uppercase">{b.broker}</span>
                <span className={`text-xl font-bold ${b.totalProfit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                  ${b.totalProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}
                </span>
                <span className="text-[10px] text-gray-500">
                  {b.totalTrades} trades | {b.accounts.length} account{b.accounts.length > 1 ? 's' : ''} | Balance: ${b.totalBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                </span>
              </div>
            ))}
          </div>

          {/* Visual Bar Chart */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Profit by Broker</h3>
            <ResponsiveContainer width="100%" height={Math.max(150, sorted.length * 50)}>
              <BarChart data={sorted} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                <YAxis type="category" dataKey="broker" tick={{ fill: '#9ca3af', fontSize: 11 }} width={100} />
                <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8 }} />
                <Bar dataKey="totalProfit" radius={[0, 4, 4, 0]}>
                  {sorted.map((entry, i) => (
                    <Cell key={i} fill={entry.totalProfit >= 0 ? '#00ff88' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Broker Comparison Table */}
          <div className="card glass-card overflow-x-auto">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Broker Comparison Table</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-dark-border text-left text-xs">
                  <th className="py-2 px-3">Broker</th>
                  <th className="py-2 px-3">Accounts</th>
                  <th className="py-2 px-3">Balance</th>
                  <th className="py-2 px-3">Net Profit</th>
                  <th className="py-2 px-3">Trades</th>
                  <th className="py-2 px-3">Win Rate</th>
                  <th className="py-2 px-3">Commission</th>
                  <th className="py-2 px-3">Swap</th>
                  <th className="py-2 px-3">Max DD</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b, i) => (
                  <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-hover">
                    <td className="py-2 px-3 font-bold">{b.broker}</td>
                    <td className="py-2 px-3">{b.accounts.length}</td>
                    <td className="py-2 px-3">${b.totalBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className={`py-2 px-3 font-semibold ${b.totalProfit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                      ${b.totalProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </td>
                    <td className="py-2 px-3">{b.totalTrades}</td>
                    <td className="py-2 px-3 text-accent-green">
                      {b.totalTrades > 0 ? ((b.totalWins / b.totalTrades) * 100).toFixed(1) : 0}%
                    </td>
                    <td className="py-2 px-3 text-accent-orange">${b.totalCommission.toFixed(2)}</td>
                    <td className="py-2 px-3 text-accent-red">${b.totalSwap.toFixed(2)}</td>
                    <td className="py-2 px-3 text-accent-red">${b.maxDrawdown.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Key Insights */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Key Insights</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
                <span className="text-[10px] text-gray-500 uppercase">Most Profitable Broker</span>
                <span className="text-sm font-bold text-accent-green block mt-1">
                  {sorted[0]?.broker || '-'} (${(sorted[0]?.totalProfit || 0).toFixed(2)})
                </span>
              </div>
              <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
                <span className="text-[10px] text-gray-500 uppercase">Lowest Costs</span>
                <span className="text-sm font-bold text-accent-purple block mt-1">
                  {[...brokerData].sort((a, b) => (Math.abs(a.totalCommission) + Math.abs(a.totalSwap)) - (Math.abs(b.totalCommission) + Math.abs(b.totalSwap)))[0]?.broker || '-'}
                </span>
              </div>
              <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
                <span className="text-[10px] text-gray-500 uppercase">Most Trades</span>
                <span className="text-sm font-bold text-white block mt-1">
                  {[...brokerData].sort((a, b) => b.totalTrades - a.totalTrades)[0]?.broker || '-'} ({[...brokerData].sort((a, b) => b.totalTrades - a.totalTrades)[0]?.totalTrades || 0})
                </span>
              </div>
              <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
                <span className="text-[10px] text-gray-500 uppercase">Best Win Rate</span>
                <span className="text-sm font-bold text-accent-green block mt-1">
                  {[...brokerData].sort((a, b) => (b.totalTrades > 0 ? b.totalWins/b.totalTrades : 0) - (a.totalTrades > 0 ? a.totalWins/a.totalTrades : 0))[0]?.broker || '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {subTab === 'costs' && (
        <div className="space-y-6 animate-stagger">
          {/* Cost Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sorted.map(b => (
              <div key={b.broker} className="stat-card card-glow">
                <span className="text-xs text-gray-500 uppercase">{b.broker}</span>
                <span className="text-lg font-bold text-accent-orange">
                  ${(Math.abs(b.totalCommission) + Math.abs(b.totalSwap)).toFixed(2)}
                </span>
                <span className="text-[10px] text-gray-500">Total Trading Costs</span>
              </div>
            ))}
          </div>

          {/* Commission & Swap Comparison Table */}
          <div className="card glass-card overflow-x-auto">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Commission & Swap Comparison</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-dark-border text-left text-xs">
                  <th className="py-2 px-3">Broker</th>
                  <th className="py-2 px-3">Commission</th>
                  <th className="py-2 px-3">Swap</th>
                  <th className="py-2 px-3">Total Costs</th>
                  <th className="py-2 px-3">Cost/Trade</th>
                  <th className="py-2 px-3">Net After Costs</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b, i) => {
                  const totalCosts = Math.abs(b.totalCommission) + Math.abs(b.totalSwap);
                  const costPerTrade = b.totalTrades > 0 ? totalCosts / b.totalTrades : 0;
                  return (
                    <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-hover">
                      <td className="py-2 px-3 font-bold">{b.broker}</td>
                      <td className="py-2 px-3 text-accent-orange">${Math.abs(b.totalCommission).toFixed(2)}</td>
                      <td className="py-2 px-3 text-accent-red">${Math.abs(b.totalSwap).toFixed(2)}</td>
                      <td className="py-2 px-3 font-semibold text-accent-orange">${totalCosts.toFixed(2)}</td>
                      <td className="py-2 px-3 text-gray-300">${costPerTrade.toFixed(2)}</td>
                      <td className={`py-2 px-3 font-semibold ${b.totalProfit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                        ${b.totalProfit.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cost Breakdown Visual */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Cost Breakdown by Broker</h3>
            <div className="space-y-4 animate-stagger">
              {sorted.map(b => {
                const totalCosts = Math.abs(b.totalCommission) + Math.abs(b.totalSwap);
                const maxCost = Math.max(...sorted.map(x => Math.abs(x.totalCommission) + Math.abs(x.totalSwap)), 1);
                const commPct = totalCosts > 0 ? (Math.abs(b.totalCommission) / totalCosts) * 100 : 0;
                return (
                  <div key={b.broker}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{b.broker}</span>
                      <span className="text-xs text-gray-400">${totalCosts.toFixed(2)}</span>
                    </div>
                    <div className="h-4 bg-dark-border rounded-full overflow-hidden flex">
                      <div className="h-full bg-accent-orange/70" style={{ width: `${(Math.abs(b.totalCommission) / maxCost) * 100}%` }} title={`Commission: $${Math.abs(b.totalCommission).toFixed(2)}`} />
                      <div className="h-full bg-accent-red/70" style={{ width: `${(Math.abs(b.totalSwap) / maxCost) * 100}%` }} title={`Swap: $${Math.abs(b.totalSwap).toFixed(2)}`} />
                    </div>
                    <div className="flex gap-4 mt-1">
                      <span className="text-[10px] text-accent-orange">Commission: ${Math.abs(b.totalCommission).toFixed(2)} ({commPct.toFixed(0)}%)</span>
                      <span className="text-[10px] text-accent-red">Swap: ${Math.abs(b.totalSwap).toFixed(2)} ({(100 - commPct).toFixed(0)}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {subTab === 'per-ea' && (
        <div className="space-y-6 animate-stagger">
          {/* Header + Dropdown */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-300 mb-1">Per-EA Broker Comparison</h3>
            <p className="text-[10px] text-gray-500 mb-4">See how the same EA performs across different brokers.</p>
            <div className="flex flex-wrap items-center gap-3">
              <select value={selectedEA} onChange={e => setSelectedEA(e.target.value)}
                className="bg-dark-bg border border-dark-border rounded-lg px-4 py-2.5 text-sm text-gray-200 w-64 appearance-none cursor-pointer"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' fill=\'%236b7280\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M8 11L3 6h10l-5 5z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
              >
                {multiBrokerEAs.length > 0 && <optgroup label="ON MULTIPLE BROKERS">
                  {multiBrokerEAs.map(ea => <option key={ea.ea_name} value={ea.ea_name}>{ea.ea_name.toUpperCase()}</option>)}
                </optgroup>}
                {singleBrokerEAs.length > 0 && <optgroup label="SINGLE BROKER ONLY">
                  {singleBrokerEAs.map(ea => <option key={ea.ea_name} value={ea.ea_name}>{ea.ea_name.toUpperCase()}</option>)}
                </optgroup>}
              </select>
              {selectedData && selectedData.total_brokers > 1 && (
                <span className="text-accent-green text-lg">&#10004;</span>
              )}
            </div>
          </div>

          {/* Per-EA Broker Comparison Table */}
          {selectedData && selectedData.brokers && (
            <div className="card glass-card overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b border-dark-border text-left">
                    <th className="py-3 px-3">BROKER</th>
                    <th className="py-3 px-3">TRADES</th>
                    <th className="py-3 px-3">WIN RATE</th>
                    <th className="py-3 px-3">PF</th>
                    <th className="py-3 px-3">NET PROFIT</th>
                    <th className="py-3 px-3">TOTAL PIPS</th>
                    <th className="py-3 px-3">PROFIT/TRADE</th>
                    <th className="py-3 px-3">COMMISSION</th>
                    <th className="py-3 px-3">SWAP</th>
                    <th className="py-3 px-3">TOTAL COSTS</th>
                    <th className="py-3 px-3">COST/TRADE</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedData.brokers.map((b, i) => (
                    <tr key={b.account_id} className={`border-b border-dark-border/30 ${i === 0 && selectedData.total_brokers > 1 ? 'border-l-2 border-l-accent-green' : ''}`}>
                      <td className="py-3 px-3 font-bold">{b.broker}</td>
                      <td className="py-3 px-3">{b.trades}</td>
                      <td className={`py-3 px-3 font-bold ${b.win_rate >= 60 ? 'text-accent-green' : b.win_rate >= 50 ? 'text-accent-orange' : 'text-accent-red'}`}>{b.win_rate}%</td>
                      <td className="py-3 px-3">{b.profit_per_lot || '-'}</td>
                      <td className={`py-3 px-3 font-bold ${b.profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                        {b.profit >= 0 ? '+' : ''}${Math.abs(b.profit).toFixed(2)}
                      </td>
                      <td className={`py-3 px-3 ${b.total_pips >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                        {b.total_pips >= 0 ? '+' : ''}{b.total_pips}
                      </td>
                      <td className={`py-3 px-3 ${b.profit_per_trade >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>${b.profit_per_trade}</td>
                      <td className="py-3 px-3 text-accent-red">${b.total_commission}</td>
                      <td className={`py-3 px-3 ${b.total_swap >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>${b.total_swap}</td>
                      <td className="py-3 px-3 text-accent-orange">${b.net_cost}</td>
                      <td className="py-3 px-3 text-accent-orange">${b.cost_per_trade}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Best Broker Per EA */}
          {multiBrokerEAs.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-300 mb-1">Best Broker Per EA</h3>
              <p className="text-[10px] text-gray-500 mb-4">Which broker gives the best profit/trade for each EA running on multiple brokers.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {multiBrokerEAs.map(ea => (
                  <div key={ea.ea_name} className="bg-dark-bg rounded-lg p-4 border border-dark-border">
                    <span className="text-xs font-bold text-white block mb-3">{ea.ea_name}</span>
                    {ea.brokers.map((b, idx) => (
                      <div key={b.account_id} className="flex items-center justify-between text-[11px] py-1.5 border-b border-dark-border/30 last:border-0">
                        <div className="flex items-center gap-2">
                          {idx === 0 && <span className="text-accent-green font-bold">&#9733;</span>}
                          <span className={idx === 0 ? 'text-accent-green font-bold' : 'text-gray-400'}>{b.broker}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`font-bold ${b.profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                            {b.profit >= 0 ? '+' : ''}${b.profit.toFixed(0)}
                          </span>
                          <span className="text-gray-500 text-[10px]">{b.trades}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* === RISK & SCORE SUB-TAB === */}
      {subTab === 'risk' && (
        <div className="space-y-6 animate-stagger">
          {/* Broker Score Cards */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-300 mb-1">Broker Efficiency Score</h3>
            <p className="text-[10px] text-gray-500 mb-4">Weighted score: Profit 40% | Costs 25% | Win Rate 20% | Drawdown 15%</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {sorted.map(b => {
                const wr = b.totalTrades > 0 ? b.totalWins / b.totalTrades * 100 : 0;
                const ppt = b.totalTrades > 0 ? b.totalProfit / b.totalTrades : 0;
                const costPct = b.totalProfit !== 0 ? Math.abs(b.totalCommission + b.totalSwap) / Math.abs(b.totalProfit) * 100 : 100;
                const profitScore = Math.min(100, Math.max(0, (ppt + 5) * 10));
                const costScore = Math.max(0, 100 - costPct);
                const wrScore = wr;
                const ddScore = Math.max(0, 100 - (b.maxDrawdown / Math.max(b.totalBalance, 1) * 100));
                const totalScore = (profitScore * 0.4 + costScore * 0.25 + wrScore * 0.2 + ddScore * 0.15).toFixed(0);
                const stars = Math.min(5, Math.max(1, Math.round(totalScore / 20)));
                return (
                  <div key={b.broker} className="bg-dark-bg rounded-lg p-4 border border-dark-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold">{b.broker}</span>
                      <span className="text-accent-purple text-lg font-bold">{totalScore}</span>
                    </div>
                    <div className="text-accent-orange text-sm mb-3">{'★'.repeat(stars)}{'☆'.repeat(5 - stars)}</div>
                    <div className="space-y-2">
                      <div><div className="flex justify-between text-[10px] text-gray-400 mb-0.5"><span>Profit</span><span>{profitScore.toFixed(0)}/100</span></div><div className="h-1.5 bg-dark-border rounded-full"><div className="h-full bg-accent-green rounded-full" style={{width:`${profitScore}%`}}/></div></div>
                      <div><div className="flex justify-between text-[10px] text-gray-400 mb-0.5"><span>Costs</span><span>{costScore.toFixed(0)}/100</span></div><div className="h-1.5 bg-dark-border rounded-full"><div className="h-full bg-accent-orange rounded-full" style={{width:`${Math.max(0,costScore)}%`}}/></div></div>
                      <div><div className="flex justify-between text-[10px] text-gray-400 mb-0.5"><span>Win Rate</span><span>{wrScore.toFixed(0)}/100</span></div><div className="h-1.5 bg-dark-border rounded-full"><div className="h-full bg-accent-purple rounded-full" style={{width:`${wrScore}%`}}/></div></div>
                      <div><div className="flex justify-between text-[10px] text-gray-400 mb-0.5"><span>Safety</span><span>{ddScore.toFixed(0)}/100</span></div><div className="h-1.5 bg-dark-border rounded-full"><div className="h-full bg-accent-blue rounded-full" style={{width:`${Math.max(0,ddScore)}%`}}/></div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Risk Metrics Table */}
          <div className="card glass-card overflow-x-auto">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Risk Metrics Comparison</h3>
            <table className="w-full text-xs">
              <thead><tr className="text-gray-500 border-b border-dark-border text-left">
                <th className="py-2 px-3">Broker</th><th className="py-2 px-3">Max DD</th><th className="py-2 px-3">DD % of Balance</th><th className="py-2 px-3">Profit Factor</th><th className="py-2 px-3">Avg Win</th><th className="py-2 px-3">Avg Loss</th><th className="py-2 px-3">Risk/Reward</th><th className="py-2 px-3">Expectancy</th>
              </tr></thead>
              <tbody>
                {sorted.map(b => {
                  const avgWin = b.totalWins > 0 ? b.totalProfit / b.totalWins : 0;
                  const avgLoss = b.totalLosses > 0 ? (b.totalProfit - (b.totalWins > 0 ? b.totalProfit : 0)) / b.totalLosses : 0;
                  const pf = b.totalLosses > 0 ? (b.totalWins / b.totalLosses).toFixed(2) : '-';
                  const ddPct = b.totalBalance > 0 ? (b.maxDrawdown / b.totalBalance * 100).toFixed(1) : '0';
                  const expectancy = b.totalTrades > 0 ? (b.totalProfit / b.totalTrades).toFixed(2) : '0';
                  return (
                    <tr key={b.broker} className="border-b border-dark-border/30">
                      <td className="py-3 px-3 font-bold">{b.broker}</td>
                      <td className="py-3 px-3 text-accent-red">${b.maxDrawdown.toFixed(2)}</td>
                      <td className={`py-3 px-3 ${parseFloat(ddPct) < 10 ? 'text-accent-green' : parseFloat(ddPct) < 25 ? 'text-accent-orange' : 'text-accent-red'}`}>{ddPct}%</td>
                      <td className="py-3 px-3">{pf}</td>
                      <td className="py-3 px-3 text-accent-green">${avgWin.toFixed(2)}</td>
                      <td className="py-3 px-3 text-accent-red">${avgLoss.toFixed(2)}</td>
                      <td className="py-3 px-3">{avgLoss !== 0 ? (Math.abs(avgWin / avgLoss)).toFixed(2) : '-'}</td>
                      <td className={`py-3 px-3 font-bold ${parseFloat(expectancy) >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>${expectancy}</td>
                    </tr>);
                })}
              </tbody>
            </table>
          </div>

          {/* Switch Recommendation */}
          {sorted.length > 1 && (
            <div className="card border-accent-green/20 bg-gradient-to-r from-accent-green/5 to-transparent">
              <h3 className="text-sm font-semibold text-accent-green mb-2">Switch Recommendation</h3>
              <p className="text-xs text-gray-400 mb-3">If all trades were executed on the best broker:</p>
              {(() => {
                const best = sorted[0];
                const bestPPT = best.totalTrades > 0 ? best.totalProfit / best.totalTrades : 0;
                const totalAllTrades = sorted.reduce((s, b) => s + b.totalTrades, 0);
                const currentTotal = sorted.reduce((s, b) => s + b.totalProfit, 0);
                const projectedTotal = totalAllTrades * bestPPT;
                const extraProfit = projectedTotal - currentTotal;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><span className="text-[10px] text-gray-500 block">Current Total Profit</span><span className="text-sm font-bold">${currentTotal.toFixed(2)}</span></div>
                    <div><span className="text-[10px] text-gray-500 block">Projected (All on {best.broker})</span><span className="text-sm font-bold text-accent-green">${projectedTotal.toFixed(2)}</span></div>
                    <div><span className="text-[10px] text-gray-500 block">Extra Profit Potential</span><span className="text-sm font-bold text-accent-purple">+${extraProfit.toFixed(2)}</span></div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* === SESSIONS SUB-TAB === */}
      {subTab === 'sessions' && (
        <BrokerSessionsSection sorted={sorted} accounts={accounts} accountType={accountType} />
      )}
    </div>
  );
}
// === BROKER SESSIONS SECTION ===
function BrokerSessionsSection({ sorted, accounts, accountType }) {
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const scopedAccounts = accountType === 'real' ? (accounts || []).filter(a => !a.is_demo) : accountType === 'demo' ? (accounts || []).filter(a => a.is_demo) : (accounts || []);
    if (!scopedAccounts || scopedAccounts.length === 0) return;
    Promise.all(
      scopedAccounts.map(a => apiGet(`/reports/by-hour?period=all&account=${a.id}${reportTypeParam(accountType)}`).then(d => ({ data: d, account: a })))
    ).then(results => {
      const brokerSessions = {};
      results.forEach(r => {
        const broker = r.account.broker || 'Unknown';
        if (!brokerSessions[broker]) brokerSessions[broker] = { asian: { profit: 0, trades: 0 }, london: { profit: 0, trades: 0 }, newyork: { profit: 0, trades: 0 } };
        (r.data || []).forEach(h => {
          if (h.hour >= 0 && h.hour < 9) { brokerSessions[broker].asian.profit += h.profit; brokerSessions[broker].asian.trades += h.trades; }
          else if (h.hour >= 9 && h.hour < 17) { brokerSessions[broker].london.profit += h.profit; brokerSessions[broker].london.trades += h.trades; }
          else { brokerSessions[broker].newyork.profit += h.profit; brokerSessions[broker].newyork.trades += h.trades; }
        });
      });
      setSessionData(brokerSessions);
    });
  }, [accounts, accountType]);

  if (!sessionData) return <div className="text-gray-500 text-center py-8">Loading session data...</div>;

  return (
    <div className="space-y-6 animate-stagger">
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-1">Session Performance by Broker</h3>
        <p className="text-[10px] text-gray-500 mb-4">Which broker performs best in each trading session.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-gray-500 border-b border-dark-border text-left">
              <th className="py-2 px-3">Broker</th>
              <th className="py-2 px-3">Asian (00-09)</th><th className="py-2 px-3">Trades</th>
              <th className="py-2 px-3">London (09-17)</th><th className="py-2 px-3">Trades</th>
              <th className="py-2 px-3">New York (17-24)</th><th className="py-2 px-3">Trades</th>
              <th className="py-2 px-3">Best Session</th>
            </tr></thead>
            <tbody>
              {Object.entries(sessionData).map(([broker, sessions]) => {
                const best = [
                  { name: 'Asian', ...sessions.asian },
                  { name: 'London', ...sessions.london },
                  { name: 'New York', ...sessions.newyork },
                ].sort((a, b) => b.profit - a.profit)[0];
                return (
                  <tr key={broker} className="border-b border-dark-border/30">
                    <td className="py-3 px-3 font-bold">{broker}</td>
                    <td className={`py-3 px-3 font-bold ${sessions.asian.profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>${sessions.asian.profit.toFixed(2)}</td>
                    <td className="py-3 px-3 text-gray-400">{sessions.asian.trades}</td>
                    <td className={`py-3 px-3 font-bold ${sessions.london.profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>${sessions.london.profit.toFixed(2)}</td>
                    <td className="py-3 px-3 text-gray-400">{sessions.london.trades}</td>
                    <td className={`py-3 px-3 font-bold ${sessions.newyork.profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>${sessions.newyork.profit.toFixed(2)}</td>
                    <td className="py-3 px-3 text-gray-400">{sessions.newyork.trades}</td>
                    <td className="py-3 px-3"><span className="text-[10px] px-2 py-0.5 rounded bg-accent-green/20 text-accent-green font-bold">{best.name}</span></td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Session Winner Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {['Asian', 'London', 'New York'].map((session, idx) => {
          const key = idx === 0 ? 'asian' : idx === 1 ? 'london' : 'newyork';
          const bestBroker = Object.entries(sessionData).sort((a, b) => b[1][key].profit - a[1][key].profit)[0];
          const colors = ['text-accent-orange', 'text-accent-green', 'text-accent-purple'];
          return (
            <div key={session} className="stat-card card-glow">
              <span className="text-[10px] text-gray-500 uppercase">Best in {session}</span>
              <span className={`text-sm font-bold ${colors[idx]}`}>{bestBroker?.[0] || '-'}</span>
              <span className="text-[10px] text-gray-500">${(bestBroker?.[1]?.[key]?.profit || 0).toFixed(2)} profit</span>
            </div>
          );
        })}
      </div>

      {/* Broker Health */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Broker Health Monitor</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(sorted || []).map(b => {
            const lastAccount = b.accounts?.[0];
            const marginLevel = lastAccount?.margin > 0 ? ((lastAccount?.equity || 0) / lastAccount.margin * 100).toFixed(0) : '---';
            return (
              <div key={b.broker} className="bg-dark-bg rounded-lg p-4 border border-dark-border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold">{b.broker}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-accent-green/20 text-accent-green font-bold">Online</span>
                </div>
                <div className="space-y-2 text-[11px]">
                  <div className="flex justify-between"><span className="text-gray-500">Accounts</span><span>{b.accounts.length}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Balance</span><span>${b.totalBalance.toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Margin Level</span><span className={parseFloat(marginLevel) > 500 ? 'text-accent-green' : parseFloat(marginLevel) > 200 ? 'text-accent-orange' : 'text-accent-red'}>{marginLevel}%</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Active Trades</span><span>{b.totalTrades > 0 ? 'Yes' : 'No'}</span></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// === ANNUAL STATS TAB ===
function AnnualStatsTab({ account, accountType }) {
  const [monthlyData, setMonthlyData] = useState(null);

  useEffect(() => {
    apiGet(`/reports/analytics/monthly-pnl?account=${account}${reportTypeParam(accountType)}`).then(setMonthlyData);
  }, [account, accountType]);

  // Group by year
  const yearlyData = React.useMemo(() => {
    if (!monthlyData) return {};
    const years = {};
    monthlyData.forEach(m => {
      const year = m.month.split('-')[0];
      if (!years[year]) years[year] = { months: [], totalProfit: 0, totalTrades: 0, totalWins: 0 };
      years[year].months.push(m);
      years[year].totalProfit += m.profit;
      years[year].totalTrades += m.trades;
      years[year].totalWins += m.wins;
    });
    return years;
  }, [monthlyData]);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="space-y-6 animate-stagger">
      {/* Yearly Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(yearlyData).reverse().map(([year, data]) => (
          <div key={year} className="stat-card card-glow">
            <span className="text-lg font-bold text-accent-purple">{year}</span>
            <span className={`text-xl font-bold ${data.totalProfit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
              ${data.totalProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}
            </span>
            <span className="text-[10px] text-gray-500">{data.totalTrades} trades  |  {data.totalTrades > 0 ? ((data.totalWins / data.totalTrades) * 100).toFixed(1) : 0}% WR</span>
          </div>
        ))}
      </div>

      {/* Monthly Heatmap Grid per Year */}
      {Object.entries(yearlyData).reverse().map(([year, data]) => (
        <div key={year} className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300">{year} Monthly Breakdown</h3>
            <span className={`text-sm font-bold ${data.totalProfit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
              Total: ${data.totalProfit.toFixed(2)}
            </span>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2">
            {monthNames.map((name, idx) => {
              const monthKey = `${year}-${String(idx + 1).padStart(2, '0')}`;
              const monthData = data.months.find(m => m.month === monthKey);
              const profit = monthData?.profit || 0;
              const trades = monthData?.trades || 0;
              return (
                <div key={idx} className="text-center">
                  <span className="text-[9px] text-gray-500 block mb-1">{name}</span>
                  <div className={`rounded-lg p-2 border ${profit > 0 ? 'bg-accent-green/10 border-accent-green/20' : profit < 0 ? 'bg-accent-red/10 border-accent-red/20' : 'bg-dark-bg border-dark-border'}`}>
                    <span className={`text-xs font-bold block ${profit > 0 ? 'text-accent-green' : profit < 0 ? 'text-accent-red' : 'text-gray-500'}`}>
                      {profit !== 0 ? `$${Math.abs(profit).toFixed(0)}` : '-'}
                    </span>
                    <span className="text-[8px] text-gray-500">{trades}t</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Cumulative Annual Chart */}
      {monthlyData && monthlyData.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Cumulative Growth</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={(() => {
              let cum = 0;
              return monthlyData.map(m => { cum += m.profit; return { month: m.month, cumulative: cum, monthly: m.profit }; });
            })()}>
              <defs>
                <linearGradient id="annualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00ff88" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 9 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
              <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8 }} />
              <Area type="monotone" dataKey="cumulative" stroke="#00ff88" fill="url(#annualGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// === PROJECTIONS TAB ===
function ProjectionsTab({ account, accountType }) {
  const { data: tradeSummary } = useApi('/trades/summary');
  const [monthlyData, setMonthlyData] = useState(null);

  useEffect(() => {
    apiGet(`/reports/analytics/monthly-pnl?account=${account}${reportTypeParam(accountType)}`).then(setMonthlyData);
  }, [account, accountType]);

  // Calculate projections from historical data
  const projections = React.useMemo(() => {
    if (!monthlyData || monthlyData.length === 0) return null;

    const profits = monthlyData.map(m => m.profit);
    const avgMonthly = profits.reduce((a, b) => a + b, 0) / profits.length;
    const bestMonth = Math.max(...profits);
    const worstMonth = Math.min(...profits);
    const totalProfit = profits.reduce((a, b) => a + b, 0);
    const monthsActive = profits.length;

    // Standard deviation for risk
    const mean = avgMonthly;
    const variance = profits.reduce((sum, p) => sum + Math.pow(p - mean, 2), 0) / profits.length;
    const stdDev = Math.sqrt(variance);

    // Projections
    const projectedAnnual = avgMonthly * 12;
    const conservativeAnnual = (avgMonthly - stdDev) * 12;
    const optimisticAnnual = (avgMonthly + stdDev) * 12;

    // Monthly projection chart (next 12 months)
    const projectionChart = [];
    let cumulative = totalProfit;
    for (let i = 1; i <= 12; i++) {
      cumulative += avgMonthly;
      projectionChart.push({
        month: `M+${i}`,
        conservative: Math.round(totalProfit + (avgMonthly - stdDev) * i),
        expected: Math.round(totalProfit + avgMonthly * i),
        optimistic: Math.round(totalProfit + (avgMonthly + stdDev) * i),
      });
    }

    return {
      avgMonthly, bestMonth, worstMonth, totalProfit, monthsActive,
      stdDev, projectedAnnual, conservativeAnnual, optimisticAnnual,
      projectionChart,
    };
  }, [monthlyData]);

  if (!projections) return <div className="text-gray-500 text-center py-12">Loading projections...</div>;

  return (
    <div className="space-y-6 animate-stagger">
      {/* Projection Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="AVG MONTHLY" value={`$${projections.avgMonthly.toFixed(2)}`} color={projections.avgMonthly >= 0 ? 'text-accent-green' : 'text-accent-red'} sub={`Based on ${projections.monthsActive} months`} />
        <StatBox label="PROJECTED ANNUAL" value={`$${projections.projectedAnnual.toFixed(0)}`} color="text-accent-purple" sub="At current pace" />
        <StatBox label="BEST MONTH" value={`$${projections.bestMonth.toFixed(2)}`} color="text-accent-green" />
        <StatBox label="WORST MONTH" value={`$${projections.worstMonth.toFixed(2)}`} color="text-accent-red" />
      </div>

      {/* Risk Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatBox label="CONSERVATIVE (Annual)" value={`$${projections.conservativeAnnual.toFixed(0)}`} color="text-accent-orange" sub="Avg - 1 StdDev" small />
        <StatBox label="EXPECTED (Annual)" value={`$${projections.projectedAnnual.toFixed(0)}`} color="text-accent-green" sub="Average pace" small />
        <StatBox label="OPTIMISTIC (Annual)" value={`$${projections.optimisticAnnual.toFixed(0)}`} color="text-accent-purple" sub="Avg + 1 StdDev" small />
      </div>

      {/* Projection Chart */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">12-Month Projection (Conservative / Expected / Optimistic)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={projections.projectionChart}>
            <defs>
              <linearGradient id="projGreen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00ff88" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
            <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10 }} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8 }} formatter={v => [`$${Number(v).toFixed(0)}`, '']} />
            <Area type="monotone" dataKey="optimistic" stroke="#a855f7" fill="none" strokeWidth={1} strokeDasharray="4 4" />
            <Area type="monotone" dataKey="expected" stroke="#00ff88" fill="url(#projGreen)" strokeWidth={2} />
            <Area type="monotone" dataKey="conservative" stroke="#f59e0b" fill="none" strokeWidth={1} strokeDasharray="4 4" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-6 mt-3">
          <span className="flex items-center gap-2 text-xs text-gray-400"><span className="w-3 h-0.5 bg-accent-orange inline-block" style={{borderTop: '1px dashed'}}></span> Conservative</span>
          <span className="flex items-center gap-2 text-xs text-accent-green"><span className="w-3 h-0.5 bg-accent-green inline-block"></span> Expected</span>
          <span className="flex items-center gap-2 text-xs text-gray-400"><span className="w-3 h-0.5 bg-accent-purple inline-block" style={{borderTop: '1px dashed'}}></span> Optimistic</span>
        </div>
      </div>

      {/* Monthly Consistency */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Monthly Consistency</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-gray-500">Profitable Months</span>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-3 bg-dark-border rounded-full overflow-hidden">
                <div className="h-full bg-accent-green rounded-full" style={{ width: `${monthlyData ? (monthlyData.filter(m => m.profit > 0).length / monthlyData.length * 100) : 0}%` }} />
              </div>
              <span className="text-sm font-bold text-accent-green">
                {monthlyData ? monthlyData.filter(m => m.profit > 0).length : 0}/{monthlyData?.length || 0}
              </span>
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Volatility (Std Dev)</span>
            <span className="text-lg font-bold text-accent-orange block mt-1">${projections.stdDev.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


// === QUANT TAB ===
function QuantTab({ period, account, accountType }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setPerformanceError('');
    apiGet(`/reports/performance?period=${period}&account=${account}${reportTypeParam(accountType)}`)
      .then(setData)
      .catch(() => setPerformanceError('Performance data could not be refreshed. Please retry.'))
      .finally(() => setLoading(false));
  }, [period, account]);

  if (loading) return <ChartSkeleton />;

  const stats = data?.stats || {};
  const equityCurve = data?.equity_curve || [];
  const dailyData = data?.daily_data || [];

  // --- Risk-Adjusted Returns ---
  const dailyReturns = dailyData.map(d => d.profit || 0);
  const avgReturn = dailyReturns.length > 0 ? dailyReturns.reduce((a, b) => a + b, 0) / dailyReturns.length : 0;
  const stdDev = Math.sqrt(dailyReturns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / Math.max(dailyReturns.length - 1, 1));
  const downsideReturns = dailyReturns.filter(r => r < 0);
  const downsideDev = Math.sqrt(downsideReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) / Math.max(downsideReturns.length, 1));
  const sharpe = stdDev > 0 ? (avgReturn / stdDev) * Math.sqrt(252) : 0;
  const sortino = downsideDev > 0 ? (avgReturn / downsideDev) * Math.sqrt(252) : 0;
  const annualizedReturn = avgReturn * 252;
  const maxDD = stats.max_drawdown || 1;
  const calmar = maxDD > 0 ? Math.abs(annualizedReturn / maxDD) : 0;

  // --- Monte Carlo Simulation ---
  const tradeReturns = dailyReturns.filter(r => r !== 0);
  const startingEquity = equityCurve.length > 0 ? (equityCurve[0].equity || 0) : 0;
  let mcResults = [];
  let ruinCount = 0;
  const simulations = 1000;
  for (let i = 0; i < simulations; i++) {
    const shuffled = [...tradeReturns].sort(() => Math.random() - 0.5);
    let equity = startingEquity;
    let ruined = false;
    for (const r of shuffled) {
      equity += r;
      if (equity <= 0) { ruined = true; break; }
    }
    mcResults.push(equity);
    if (ruined) ruinCount++;
  }
  mcResults.sort((a, b) => a - b);
  const mc5 = mcResults[Math.floor(simulations * 0.05)] || 0;
  const mc50 = mcResults[Math.floor(simulations * 0.5)] || 0;
  const mc95 = mcResults[Math.floor(simulations * 0.95)] || 0;
  const ruinProb = ((ruinCount / simulations) * 100).toFixed(2);

  // --- Correlation Matrix ---
  const eaTrades = {};
  dailyData.forEach(d => {
    const ea = d.ea_name || 'Unknown';
    if (!eaTrades[ea]) eaTrades[ea] = [];
    eaTrades[ea].push(d.profit || 0);
  });
  const eaNames = Object.keys(eaTrades).filter(e => eaTrades[e].length > 1);
  const correlationMatrix = [];
  let totalCorr = 0, corrCount = 0;
  for (let i = 0; i < eaNames.length; i++) {
    const row = [];
    for (let j = 0; j < eaNames.length; j++) {
      if (i === j) { row.push(1); continue; }
      const a = eaTrades[eaNames[i]], b = eaTrades[eaNames[j]];
      const len = Math.min(a.length, b.length);
      const meanA = a.slice(0, len).reduce((s, v) => s + v, 0) / len;
      const meanB = b.slice(0, len).reduce((s, v) => s + v, 0) / len;
      let num = 0, denA = 0, denB = 0;
      for (let k = 0; k < len; k++) {
        num += (a[k] - meanA) * (b[k] - meanB);
        denA += Math.pow(a[k] - meanA, 2);
        denB += Math.pow(b[k] - meanB, 2);
      }
      const corr = (denA > 0 && denB > 0) ? num / Math.sqrt(denA * denB) : 0;
      row.push(corr);
      if (i < j) { totalCorr += Math.abs(corr); corrCount++; }
    }
    correlationMatrix.push(row);
  }
  const diversificationScore = corrCount > 0 ? ((1 - totalCorr / corrCount) * 100).toFixed(1) : 100;

  // --- Drawdown Analysis ---
  let peak = 0, underwaterDays = 0, maxConsecLoss = 0, consecLoss = 0;
  let drawdowns = [], currentDD = 0, ddStart = null, recoveryTimes = [];
  const underwaterChart = [];
  equityCurve.forEach((point, idx) => {
    const eq = point.equity || 0;
    if (eq > peak) {
      if (currentDD > 0 && ddStart !== null) {
        recoveryTimes.push(idx - ddStart);
      }
      peak = eq;
      currentDD = 0;
      ddStart = null;
    } else {
      const dd = ((peak - eq) / peak) * 100;
      if (ddStart === null) ddStart = idx;
      currentDD = dd;
      underwaterDays++;
      if (dd > 5) drawdowns.push(dd);
    }
    underwaterChart.push({ date: point.date, drawdown: -currentDD });
  });
  dailyData.forEach(d => {
    if ((d.profit || 0) < 0) { consecLoss++; maxConsecLoss = Math.max(maxConsecLoss, consecLoss); }
    else consecLoss = 0;
  });
  const avgRecovery = recoveryTimes.length > 0 ? Math.round(recoveryTimes.reduce((a, b) => a + b, 0) / recoveryTimes.length) : 0;
  const ddFrequency = new Set(drawdowns.map((_, i) => i)).size;

  // --- Trade Distribution ---
  const profits = dailyReturns.filter(r => r !== 0);
  const mean = profits.length > 0 ? profits.reduce((a, b) => a + b, 0) / profits.length : 0;
  const profitStd = Math.sqrt(profits.reduce((s, p) => s + Math.pow(p - mean, 2), 0) / Math.max(profits.length - 1, 1));
  const median = [...profits].sort((a, b) => a - b)[Math.floor(profits.length / 2)] || 0;
  const n = profits.length;
  const skewness = n > 2 ? (n / ((n - 1) * (n - 2))) * profits.reduce((s, p) => s + Math.pow((p - mean) / (profitStd || 1), 3), 0) : 0;
  const kurtosis = n > 3 ? ((n * (n + 1)) / ((n - 1) * (n - 2) * (n - 3))) * profits.reduce((s, p) => s + Math.pow((p - mean) / (profitStd || 1), 4), 0) - (3 * Math.pow(n - 1, 2)) / ((n - 2) * (n - 3)) : 0;
  const outliers = profits.filter(p => Math.abs(p - mean) > 2 * profitStd).length;

  const bucketCount = 20;
  const minP = Math.min(...profits, 0), maxP = Math.max(...profits, 0);
  const bucketSize = (maxP - minP) / bucketCount || 1;
  const histogram = Array.from({ length: bucketCount }, (_, i) => {
    const low = minP + i * bucketSize;
    const high = low + bucketSize;
    const count = profits.filter(p => p >= low && p < high).length;
    return { range: `${low.toFixed(0)}`, count, isPositive: low + bucketSize / 2 >= 0 };
  });

  // --- Kelly Criterion ---
  const winRate = (stats.win_rate || 0) / 100;
  const avgWin = stats.largest_win ? (stats.gross_profit || 0) / Math.max(stats.winning_trades || 1, 1) : 0;
  const avgLoss = Math.abs(stats.gross_loss || 0) / Math.max(stats.losing_trades || 1, 1);
  const kelly = avgWin > 0 ? ((winRate * avgWin - (1 - winRate) * avgLoss) / avgWin) * 100 : 0;
  const halfKelly = kelly / 2;
  const kellyMultiplier = Math.max(0, kelly / 100);
  const projectedKellyReturn = kellyMultiplier > 0 ? (stats.net_pnl || 0) * kellyMultiplier * (252 / Math.max(dailyData.length, 1)) : 0;

  return (
    <div className="space-y-6 animate-stagger">
      {/* 1. Risk-Adjusted Returns */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Risk-Adjusted Returns</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatBox label="SHARPE RATIO" value={sharpe.toFixed(2)} color={sharpe > 1 ? 'text-accent-green' : sharpe > 0 ? 'text-accent-orange' : 'text-accent-red'} sub="Risk-adjusted return" />
          <StatBox label="SORTINO RATIO" value={sortino.toFixed(2)} color={sortino > 1.5 ? 'text-accent-green' : sortino > 0 ? 'text-accent-orange' : 'text-accent-red'} sub="Downside risk only" />
          <StatBox label="CALMAR RATIO" value={calmar.toFixed(2)} color={calmar > 3 ? 'text-accent-green' : calmar > 1 ? 'text-accent-orange' : 'text-accent-red'} sub="Return / Max DD" />
        </div>
      </div>

      {/* 2. Monte Carlo Simulation */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Monte Carlo Simulation (1,000 runs)</h3>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <StatBox label="WORST CASE (5th %)" value={`$${mc5.toFixed(2)}`} color="text-accent-red" sub="5th percentile" />
          <StatBox label="MEDIAN (50th %)" value={`$${mc50.toFixed(2)}`} color="text-accent-purple" sub="Expected outcome" />
          <StatBox label="BEST CASE (95th %)" value={`$${mc95.toFixed(2)}`} color="text-accent-green" sub="95th percentile" />
        </div>
        <div className="bg-dark-bg rounded-lg p-3 text-xs text-gray-400">
          <span className="text-white font-medium">Probability of Ruin:</span> {ruinProb}% chance of account going to zero based on historical trade distribution.
          {parseFloat(ruinProb) < 1 && <span className="text-accent-green ml-2">✓ Low risk</span>}
          {parseFloat(ruinProb) >= 1 && parseFloat(ruinProb) < 5 && <span className="text-accent-orange ml-2">⚠ Moderate risk</span>}
          {parseFloat(ruinProb) >= 5 && <span className="text-accent-red ml-2">✗ High risk</span>}
        </div>
      </div>

      {/* 3. Correlation Matrix */}
      {eaNames.length > 1 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300">EA Correlation Matrix</h3>
            <div className="stat-card p-2">
              <span className="text-[10px] text-gray-500">DIVERSIFICATION SCORE</span>
              <span className={`text-sm font-bold ${parseFloat(diversificationScore) > 70 ? 'text-accent-green' : parseFloat(diversificationScore) > 40 ? 'text-accent-orange' : 'text-accent-red'}`}>{diversificationScore}%</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr>
                  <th className="p-1"></th>
                  {eaNames.map(name => <th key={name} className="p-1 text-gray-500 truncate max-w-[60px]">{name.slice(0, 8)}</th>)}
                </tr>
              </thead>
              <tbody>
                {eaNames.map((name, i) => (
                  <tr key={name}>
                    <td className="p-1 text-gray-400 font-medium truncate max-w-[60px]">{name.slice(0, 8)}</td>
                    {correlationMatrix[i].map((corr, j) => {
                      const absCorr = Math.abs(corr);
                      const bg = i === j ? 'bg-dark-bg' : corr > 0.5 ? `rgba(239,68,68,${absCorr * 0.5})` : corr < -0.2 ? `rgba(0,255,136,${absCorr * 0.5})` : `rgba(0,255,136,${(1 - absCorr) * 0.3})`;
                      return (
                        <td key={j} className="p-1 text-center rounded" style={{ backgroundColor: typeof bg === 'string' && bg.startsWith('rgba') ? bg : undefined }} >
                          <span className={i === j ? 'text-gray-600' : absCorr > 0.7 ? 'text-accent-red' : 'text-gray-300'}>{corr.toFixed(2)}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-gray-500 mt-2">Green = low correlation (good diversification), Red = high correlation (concentrated risk)</p>
        </div>
      )}

      {/* 4. Drawdown Analysis */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Deep Drawdown Analysis</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatBox label="UNDERWATER DAYS" value={underwaterDays} color="text-accent-red" sub={`of ${equityCurve.length} total`} small />
          <StatBox label="AVG RECOVERY" value={`${avgRecovery} days`} color="text-accent-orange" small />
          <StatBox label="MAX CONSEC LOSSES" value={maxConsecLoss} color="text-accent-red" small />
          <StatBox label="DD > 5% COUNT" value={ddFrequency} color="text-accent-purple" sub="Significant drawdowns" small />
        </div>
        {underwaterChart.length > 0 && (
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={underwaterChart}>
              <defs>
                <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 9 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 9 }} unit="%" />
              <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8 }} formatter={(v) => [`${v.toFixed(2)}%`, 'Drawdown']} />
              <Area type="monotone" dataKey="drawdown" stroke="#ef4444" fill="url(#ddGrad)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* 5. Trade Distribution */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Trade Distribution</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          <StatBox label="MEAN" value={`$${mean.toFixed(2)}`} color="text-white" small />
          <StatBox label="MEDIAN" value={`$${median.toFixed(2)}`} color="text-white" small />
          <StatBox label="STD DEV" value={`$${profitStd.toFixed(2)}`} color="text-accent-purple" small />
          <StatBox label="SKEWNESS" value={skewness.toFixed(2)} color={skewness > 0 ? 'text-accent-green' : 'text-accent-red'} sub={skewness > 0 ? 'Right-tailed' : 'Left-tailed'} small />
          <StatBox label="KURTOSIS" value={kurtosis.toFixed(2)} color="text-accent-orange" sub={kurtosis > 0 ? 'Fat tails' : 'Thin tails'} small />
        </div>
        {histogram.length > 0 && (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={histogram}>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="range" tick={{ fill: '#6b7280', fontSize: 9 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 9 }} />
              <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8 }} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {histogram.map((entry, i) => (
                  <Cell key={i} fill={entry.isPositive ? '#00ff88' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        <div className="mt-2 text-xs text-gray-500">
          Outlier trades (&gt;2σ): <span className="text-accent-orange font-bold">{outliers}</span> of {profits.length} trades
        </div>
      </div>

      {/* 6. Kelly Criterion */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Portfolio Optimization (Kelly Criterion)</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatBox label="FULL KELLY %" value={`${kelly.toFixed(1)}%`} color={kelly > 0 ? 'text-accent-green' : 'text-accent-red'} sub="Optimal sizing" small />
          <StatBox label="HALF KELLY %" value={`${halfKelly.toFixed(1)}%`} color="text-accent-purple" sub="Conservative (recommended)" small />
          <StatBox label="LOT MULTIPLIER" value={`${Math.max(0, halfKelly / 100).toFixed(2)}x`} color="text-accent-orange" sub="Based on half-Kelly" small />
          <StatBox label="PROJECTED ANNUAL" value={`$${projectedKellyReturn.toFixed(0)}`} color="text-accent-green" sub="If using Kelly sizing" small />
        </div>
        <div className="bg-dark-bg rounded-lg p-3 text-xs text-gray-400">
          <p><span className="text-white font-medium">How to use:</span> Kelly suggests risking {halfKelly.toFixed(1)}% (half-Kelly) of your account per trade for optimal growth while managing risk.</p>
          {kelly < 0 && <p className="text-accent-red mt-1">⚠ Negative Kelly indicates the strategy has negative expected value. Consider reducing position size.</p>}
          {kelly > 25 && <p className="text-accent-orange mt-1">⚠ Very high Kelly values suggest using half or quarter Kelly for safety.</p>}
        </div>
      </div>
    </div>
  );
}


// === SHARED COMPONENTS ===

function StatBox({ label, value, color = 'text-white', sub, small }) {
  return (
    <div className={`stat-card ${small ? 'p-3' : ''}`}>
      <span className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</span>
      <span className={`${small ? 'text-sm' : 'text-lg'} font-bold ${color}`}>{value}</span>
      {sub && <span className="text-[10px] text-gray-500">{sub}</span>}
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}


